import { Product } from '../../../common/js/static/Product';
import { Utils } from '../../../common/js/Utils';
import { CoverpathPolicy } from '../../classes/coverpath_subclasses/CoverpathPolicy';
import { MMDirectPolicy } from '../../classes/mmdirect_subclasses/MMDirectPolicy';
import { CoverpathUtils } from '../../js/static/CoverpathUtils';
import { CACPaymentData } from './CACPaymentData';
import { LoanType } from '../../js/interfaces/CoverpathTypes';
import { LifePolicyInfoDTO } from '@hit/policy-admin-types/dist/data-model/Types';

export class PolicyData {
  private cacPaymentData: CACPaymentData;
  private advisorSignedApp: boolean = false;
  private hasJointOwner: boolean = false;
  private faceExceedsMaxTLICCoverage: boolean = false;
  private hasPendingAmendment: boolean = false;
  private insurabilitySignatureIsRequired: boolean = false;
  private isTLIC: boolean = false;
  private formattedMaxTLICCoverage: string;
  private customerSignedApp: boolean;
  private isOwnerSameAsInsured: boolean;
  private formattedOfferRequiredBy: string;
  private isPolicyTableRated: boolean;
  private formattedFreeLookEndsOn: string;
  private insuredFullName: string;
  private insuredFirstName: string;
  private insuredAddressLine1: string;
  private maxTLICCoverage: number;
  private formattedParamedRequiredBy: string;
  private contactNumber: string;
  private workflowAction: string;
  private advisorDescriptiveNoun: string;
  private isTermConversionPolicy: boolean;
  private isWholeConversionPolicy: boolean;
  private hasAgent: boolean;
  private isFrozen: boolean;
  private insuredEsignRequired: boolean = false;
  private esignMetadata;
  private ownerCompletedDocusign: boolean = false;
  private waitingOnOtherSigners: boolean = false;
  private waitingOnInsuredToSign: boolean = false;
  private waitingOnSecondOwnerToSign: boolean = false;
  private signatureNeeded: boolean = false;
  private isAccelerated: boolean = false;
  private isLiteTouch: boolean = false;
  private isBlockedByAdvisor: boolean;
  private insuredIsJuvenile: boolean;
  private onHold: boolean;
  private ownerFullName: string;
  private ownerFirstName: string;
  private otherOwnerFullName: string;
  private isAnnuity: boolean;
  private termWithPendingAmendment: number;
  private isWholeLife: boolean;
  private isCoverpathPolicy: boolean;
  private isJointApp: boolean;
  private isInForce: boolean;
  private finalOfferSentDate: Date;
  private isLifeInsurancePolicy: boolean;
  private applicationSignedDate: Date;
  private ownerPersonResidentialAddressState: string;
  private convertedTo: string;
  private isTermLifePolicy: boolean;
  private ownerType: string;
  private insuredType: string;
  private wasCanceledDeclinedRejected: boolean;
  private isLifeBridge: boolean;
  private isEcpProduct: boolean;
  private latestAmendmentDate: Date;
  private agentDisplayName: string;
  private workflowState: string;
  private isPrimary: boolean;
  private hasWaiver: boolean;
  private isAnnuallyRenewableTerm: boolean;
  private policyType: string;
  private applicationType: string;
  private isSecondary: boolean;
  private hasAnyInForce: boolean;
  private dividendOption: string;
  private loanInterestType: LoanType;
  private isWholeAPL: boolean;
  private finalOfferAcceptedDate: Date;
  private isMMDirectPolicy: boolean;
  private hasTableRating: boolean;
  private offerRequiredBy: Date;
  private paramedRequiredBy: Date;
  private freeLookEndsOn: Date;
  private workflowSubState: string;
  private convertedFrom: string;
  private issuedDate: Date;
  private insuredDOB: Date;
  private insuredGender: '1' | '2';
  private ratingCode: string;
  private productType: string;
  private product: string;
  private requiresSpecialHandling: boolean;
  private isLiteTouchApproved: boolean;
  private insuredSignerEmail: string;
  private insuredEmail: string;
  private otherOwnerEmail: string;
  private insuredSignerFullName: string;
  private insuredSignerFirstName: string;
  private includeAdbr: boolean;
  private isSanderlingPolicy: boolean;
  public isInGracePeriod: boolean;
  private isCanceled: boolean;
  private isTerminated: boolean;
  private hasPAStatusForPolicy: boolean;
  private productPremiumVersion: string;

  constructor(policy: CoverpathPolicy | MMDirectPolicy, cacPaymentData: CACPaymentData) {
    this.cacPaymentData = cacPaymentData;
    this.advisorDescriptiveNoun = CoverpathUtils.getAdvisorDescriptiveNoun();
    this.isPolicyTableRated = policy.hasTableRating();
    this.hasPendingAmendment = policy.hasPendingAmendment();
    //Insured Info
    this.insurabilitySignatureIsRequired = CoverpathUtils.isInsurabilityStatementSignatureRequired(policy);
    this.insuredFullName = policy.application.primaryPolicy.insured.person.getFullName();
    this.insuredFirstName = policy.application.primaryPolicy.insured.person.firstName;
    this.insuredEmail = policy.application.primaryPolicy.insured.person.email;
    this.insuredAddressLine1 = policy.application.primaryPolicy.insured.address.line1;

    const insuredSigner = policy.application.primaryPolicy.insured.getInsuredSigner();
    if (insuredSigner) {
      this.insuredSignerEmail = insuredSigner.email;
      this.insuredSignerFullName = insuredSigner.fullName;
      this.insuredSignerFirstName = insuredSigner.firstName;
    }
    this.isOwnerSameAsInsured = policy.application.primaryPolicy.isOwnerSameAsInsured();
    this.insuredType = policy.application.primaryPolicy.insuredType;

    //Owners Info
    if (policy.application.primaryPolicy.ownerPerson) {
      this.ownerFullName = policy.application.primaryPolicy.ownerPerson.getFullName();
      this.ownerFirstName = policy.application.primaryPolicy.ownerPerson.firstName;
    } else if (policy.application.primaryPolicy.ownerEntity) {
      this.ownerFullName = policy.application.primaryPolicy.ownerEntity.getFullNameOfOwner();
      this.ownerFirstName = policy.application.primaryPolicy.ownerEntity.name;
    } else {
      this.ownerFullName = '';
      this.ownerFirstName = '';
    }
    this.ownerPersonResidentialAddressState = policy.application.primaryPolicy.ownerPersonResidentialAddress
      ? policy.application.primaryPolicy.ownerPersonResidentialAddress.state
      : '';
    this.ownerType = policy.ownerType;

    //Other Owner Info
    this.otherOwnerFullName = policy.application.primaryPolicy.otherOwner
      ? policy.application.primaryPolicy.otherOwner.getFullName()
      : '';
    this.otherOwnerEmail = policy.application.primaryPolicy.otherOwner
      ? policy.application.primaryPolicy.otherOwner.email
      : '';

    this.isTLIC = policy.isTLIC();
    this.formattedOfferRequiredBy = policy.application.primaryPolicy.offerRequiredBy
      ? policy.application.primaryPolicy.offerRequiredBy.toLocaleDateString()
      : '';
    this.formattedParamedRequiredBy = policy.application.primaryPolicy.paramedRequiredBy
      ? policy.application.primaryPolicy.paramedRequiredBy.toLocaleDateString()
      : '';
    this.formattedFreeLookEndsOn = policy.freeLookEndsOn ? policy.freeLookEndsOn.toLocaleDateString() : '';
    this.esignMetadata = policy.application.primaryPolicy.getOwnerSigningPerson().esignMetadata;
    this.ownerCompletedDocusign = this.esignMetadata && this.esignMetadata.ownerSignComplete;
    this.waitingOnOtherSigners =
      this.esignMetadata && this.ownerCompletedDocusign && this.esignMetadata.waitingOnOtherSigners;
    this.waitingOnInsuredToSign = this.esignMetadata && this.esignMetadata.waitingOnInsured;
    this.waitingOnSecondOwnerToSign = this.esignMetadata && this.esignMetadata.waitingOnSecondOwner;
    this.signatureNeeded = !this.customerSignedApp && !this.ownerCompletedDocusign;
    const totalFaceAmount = cacPaymentData.getCACFaceAmount();
    this.maxTLICCoverage = Product.products.CASTerm.productSpecs.maxFaceForTLIC;
    this.formattedMaxTLICCoverage = Utils.formatCurrency(this.maxTLICCoverage, 0);
    this.faceExceedsMaxTLICCoverage = totalFaceAmount > this.maxTLICCoverage;
    this.workflowAction = `${policy.workflowState}:${policy.workflowSubState}`;
    this.isTermConversionPolicy = !!policy.convertedTo;
    this.isWholeConversionPolicy = !!policy.convertedFrom;
    this.isFrozen = policy.application.isFrozen;
    this.hasAgent = policy.application.primaryPolicy.primaryPolicyAgent != null;
    this.isAccelerated = policy.isAccelerated();
    this.isLiteTouch = policy.application.primaryPolicy.isLiteTouch;
    this.insuredIsJuvenile = policy.application.primaryPolicy.insuredIsJuvenile();
    this.onHold = this.isAccelerated && this.isFrozen;
    this.hasJointOwner = policy.application.primaryPolicy.isJointOwnership();
    this.isAnnuity = policy.isAnnuity();
    this.termWithPendingAmendment = policy.termWithPendingAmendment();
    this.isWholeLife = policy.isWholeLifePolicyWithPendingAmendment();
    this.isCoverpathPolicy = policy.isCoverpathPolicy();
    this.isJointApp = policy.application.isJointAppWithPendingAmendment();
    this.isInForce = policy.isInForce();
    this.finalOfferSentDate = policy.application.finalOfferSentDate;
    this.isLifeInsurancePolicy = policy.isLifeInsurancePolicy();
    this.applicationSignedDate = policy.application.primaryPolicy.applicationSignedDate;
    this.customerSignedApp = this.applicationSignedDate !== null;
    this.convertedTo = policy.convertedTo;
    this.isTermLifePolicy = policy.isTermLifePolicyWithPendingAmendment();
    this.wasCanceledDeclinedRejected = policy.wasCanceledDeclinedRejected();
    this.isLifeBridge = policy.isLifeBridge();
    this.isEcpProduct = policy.isEcpProductWithPendingAmendment();
    this.latestAmendmentDate = policy.getLatestAmendmentDate();
    this.agentDisplayName = policy.application.primaryPolicy.primaryPolicyAgent
      ? policy.application.primaryPolicy.primaryPolicyAgent.primaryAgent.displayNameWithLastName
      : '';
    this.workflowState = policy.workflowState;
    this.isPrimary = policy.isPrimaryWithPendingAmendment;
    this.setCACWavierOfPremium(policy.hasWaiverWithPendingAmendment());
    this.isAnnuallyRenewableTerm = policy.isAnnuallyRenewableTerm();
    this.policyType = policy.typeWithPendingAmendment();
    this.applicationType = policy.application.primaryPolicy.typeWithPendingAmendment();
    this.isSecondary = policy.isSecondaryWithPendingAmendment;
    this.hasAnyInForce = policy.application.hasAnyInForce();
    if (this.isCoverpathPolicy) {
      this.dividendOption = (policy as CoverpathPolicy).dividendOptionWithPendingAmendment();
      this.loanInterestType = (policy as CoverpathPolicy).loanInterestTypeWithPendingAmendment();
      this.isWholeAPL = (policy as CoverpathPolicy).wholeAPLWithPendingAmendment();
      this.advisorSignedApp = (policy as CoverpathPolicy).application.agentEsignComplete;
      this.setCACIncludeAdbr((policy as CoverpathPolicy).includeAdbr);
    }
    this.contactNumber = Product.channels.CAS.phone;
    this.insuredEsignRequired = CoverpathUtils.isInsuredSignRequiredAtFinalAcceptance(policy.application.primaryPolicy);
    this.isBlockedByAdvisor =
      (this.customerSignedApp || this.ownerCompletedDocusign) && !this.advisorSignedApp && this.hasAgent;
    this.finalOfferAcceptedDate = policy.application.finalOfferSentDate;
    this.isMMDirectPolicy = policy.isMMDirectPolicy();
    this.hasTableRating = policy.hasTableRating();
    this.offerRequiredBy = policy.application.primaryPolicy.offerRequiredBy;
    this.paramedRequiredBy = policy.application.primaryPolicy.paramedRequiredBy;
    this.freeLookEndsOn = policy.freeLookEndsOn;
    this.workflowSubState = policy.workflowSubState;
    this.convertedFrom = policy.convertedFrom;
    this.issuedDate = policy.issuedDate;
    this.insuredDOB = policy.application.primaryPolicy.insured.person.dob;
    this.insuredGender = policy.application.primaryPolicy.insured.person.gender;
    this.ratingCode = policy.finalRateClass || policy.tLICRateClass;
    this.productType = policy.productTypeWithPendingAmendment();
    this.product = policy.productWithPendingAmendment();
    this.requiresSpecialHandling = policy.application.primaryPolicy.requiresSpecialHandling();
    this.isLiteTouchApproved = policy.application.primaryPolicy.isLiteTouchApproved();
    this.isSanderlingPolicy = CoverpathUtils.isSanderlingPolicy(policy.productWithPendingAmendment());

    this.isCanceled = policy.wasCanceledDeclined();
    this.isTerminated = policy.wasTerminated();
    this.isInGracePeriod = policy.isInGracePeriod();
    this.productPremiumVersion = policy.productPremiumVersion;
    return this;
  }

  getCACPaymentData(): CACPaymentData {
    return this.cacPaymentData;
  }

  setCACPaymentData(data: CACPaymentData) {
    this.cacPaymentData = data;
  }

  getCACIsWholeLife() {
    return this.isWholeLife;
  }

  getCACHasPendingAmendment() {
    return this.hasPendingAmendment;
  }

  getCACIsAccelerated() {
    return this.isAccelerated;
  }

  getCACIsCoverpathPolicy() {
    return this.isCoverpathPolicy;
  }

  getCACESignMetaData() {
    return this.esignMetadata;
  }

  getCACOwnerCompletedDocusign() {
    return this.ownerCompletedDocusign;
  }

  getCACInsurabilitySignatureIsRequired() {
    return this.insurabilitySignatureIsRequired;
  }

  getCACWaitingOnOtherSigners() {
    return this.waitingOnOtherSigners;
  }

  getCACWaitingOnInsuredToSign() {
    return this.waitingOnInsuredToSign;
  }

  getCACWaitingOnSecondOwnerToSign() {
    return this.waitingOnSecondOwnerToSign;
  }

  getCACIsBlockedByAdvisor() {
    return this.isBlockedByAdvisor;
  }

  getCACSignatureNeeded() {
    return this.signatureNeeded;
  }

  getCACIsOwnerSameAsInsured() {
    return this.isOwnerSameAsInsured;
  }

  getCACTerm() {
    return this.termWithPendingAmendment;
  }

  getCACIsJointApp() {
    return this.isJointApp;
  }

  getCACIsInForce() {
    return this.isInForce;
  }

  getCACIsHidePaymentDate() {
    return this.isInGracePeriod || this.isCanceled || this.isTerminated || this.hasPAStatusForPolicy;
  }

  getCACFinalOfferSentDate(): Date {
    return this.finalOfferSentDate;
  }

  getCACInsuredFullName() {
    return this.insuredFullName;
  }

  getCACHasJointOwner() {
    return this.hasJointOwner;
  }

  getCACOwnerFullName() {
    return this.ownerFullName;
  }

  getCACOtherOwnerFullName() {
    return this.otherOwnerFullName;
  }

  getCACIsLifeInsurancePolicy() {
    return this.isLifeInsurancePolicy;
  }

  getCACApplicationSignedDate() {
    return this.applicationSignedDate;
  }

  getCACOwnerPersonResidentialAddressState() {
    return this.ownerPersonResidentialAddressState;
  }

  getCACConvertedTo() {
    return this.convertedTo;
  }

  getCACIsTermLifePolicy() {
    return this.isTermLifePolicy;
  }

  getCACInsuredFirstName() {
    return this.insuredFirstName;
  }

  getCACWasCanceledDeclinedRejected() {
    return this.wasCanceledDeclinedRejected;
  }

  getCACIsLifeBridge() {
    return this.isLifeBridge;
  }

  getCACIsEcpProduct() {
    return this.isEcpProduct;
  }

  getCACLatestAmendmentDate() {
    return this.latestAmendmentDate;
  }

  getCACAgentDisplayName() {
    return this.agentDisplayName;
  }

  getCACWorkflowState() {
    return this.workflowState;
  }

  getCACIsPrimary() {
    return this.isPrimary;
  }

  getCACHasWaiver() {
    return this.hasWaiver;
  }

  getCACIsAnnuallyRenewableTerm() {
    return this.isAnnuallyRenewableTerm;
  }

  getCACPolicyType() {
    return this.policyType;
  }

  getCACApplicationType() {
    return this.applicationType;
  }

  getCACIsSecondary() {
    return this.isSecondary;
  }

  getCACHasAnyInForce() {
    return this.hasAnyInForce;
  }

  getCACDividendOption() {
    return this.dividendOption;
  }

  getCACLoanInterestType() {
    return this.loanInterestType;
  }

  getCACIsWholeAPL() {
    return this.isWholeAPL;
  }

  getCACContactNumber() {
    return this.contactNumber;
  }

  getCACIsInsuredESignRequired() {
    return this.insuredEsignRequired;
  }

  getCACIsAdvisorSigned() {
    return this.advisorSignedApp;
  }

  getCACfinalOfferAcceptedDate() {
    return this.finalOfferAcceptedDate;
  }

  getCACIsMMDirectPolicy() {
    return this.isMMDirectPolicy;
  }

  getCACHasTableRating() {
    return this.hasTableRating;
  }

  getCACIsTLIC() {
    return this.isTLIC;
  }

  getCACOfferRequiredBy() {
    return this.offerRequiredBy;
  }

  getCACParamedRequiredBy() {
    return this.paramedRequiredBy;
  }

  getCACFreeLookEndsOn() {
    return this.freeLookEndsOn;
  }

  getCACMaxTLICCoverage() {
    return this.maxTLICCoverage;
  }

  getCACFormattedMaxTLICCoverage() {
    return this.formattedMaxTLICCoverage;
  }

  getCACFaceExceedsMaxTLICCoverage() {
    return this.faceExceedsMaxTLICCoverage;
  }

  getCACWorkflowSubState() {
    return this.workflowSubState;
  }

  getCACConvertedFrom() {
    return this.convertedFrom;
  }

  getCACIsFrozen() {
    return this.isFrozen;
  }

  getCACHasAgent() {
    return this.hasAgent;
  }

  getCACIssuedDate() {
    return this.issuedDate;
  }

  getCACInsuredDOB() {
    return this.insuredDOB;
  }

  getCACProduct() {
    return this.product;
  }

  getCACProductType() {
    return this.productType;
  }

  getCACInsuredGender() {
    return this.insuredGender;
  }

  getCACRatingCode() {
    return this.ratingCode;
  }

  getCACIsLiteTouch() {
    return this.isLiteTouch;
  }

  getCACInsuredIsJuvenile() {
    return this.insuredIsJuvenile;
  }

  getCACIsLiteTouchApproved() {
    return this.isLiteTouchApproved;
  }

  getCACRequiresSpecialHandling() {
    return this.requiresSpecialHandling;
  }

  getCACInsuredSignerEmail() {
    return this.insuredSignerEmail;
  }

  getCACInsuredEmail() {
    return this.insuredEmail;
  }

  getCACOtherOwnerEmail() {
    return this.otherOwnerEmail;
  }

  getCACInsuredSignerFullName() {
    return this.insuredSignerFullName;
  }

  getCACInsuredSignerFirstName() {
    return this.insuredSignerFirstName;
  }

  getCACCustomerSignedApp() {
    return this.customerSignedApp;
  }

  getCACIncludeAdbr() {
    return this.includeAdbr;
  }

  getCACIsSanderlingPolicy() {
    return this.isSanderlingPolicy;
  }

  getCACProductPremiumVersion() {
    return this.productPremiumVersion;
  }

  overriderPostIssueData(policyInfoPayload: LifePolicyInfoDTO) {
    this.termWithPendingAmendment = policyInfoPayload.policyInfo.duration as number;
    if (this.isCoverpathPolicy) {
      this.dividendOption = policyInfoPayload.policyInfo.dividendOption as string;
    }

    this.insuredFirstName = policyInfoPayload.policyInfo.insuredFirstName as string;
    if (!policyInfoPayload.policyInfo.insuredFirstName && !policyInfoPayload.policyInfo.insuredLastName) {
      this.insuredFullName = `${policyInfoPayload.policyInfo.insuredFirstName} ${policyInfoPayload.policyInfo.insuredLastName}`;
    }
  }

  overrideFromPAPolicy(paPolicy, paWorkflowStatus) {
    if (this.isCoverpathPolicy) {
      this.isWholeAPL = paPolicy.policy.apl;
      this.dividendOption = paPolicy.policy.product.dividendOption;
      this.loanInterestType = paPolicy.policy.loanInterestType;

      const STATUS_DELIMITER: string = ':';
      let policyStageCode, billingStageCode, policyGeneralStatus;
      const statuses = paWorkflowStatus.split(STATUS_DELIMITER);

      if (statuses && statuses.length === 1) {
        policyStageCode = statuses[0].trim();
        billingStageCode = 'NotFound';
        policyGeneralStatus = 'NotFound';
      } else if (statuses && statuses.length === 2) {
        policyStageCode = statuses[0].trim();
        billingStageCode = statuses[1].trim();
        policyGeneralStatus = 'NotFound';
      } else if (statuses && statuses.length === 3) {
        policyStageCode = statuses[0].trim();
        billingStageCode = statuses[1].trim();
        policyGeneralStatus = statuses[2].trim();
      } else {
        policyStageCode = 'NotFound';
        billingStageCode = 'NotFound';
        policyGeneralStatus = 'NotFound';
      }
      this.hasPAStatusForPolicy =
        policyStageCode === 'Grace Period' ||
        billingStageCode === 'Grace Period' ||
        policyStageCode === 'Canceled by Client' ||
        policyStageCode === 'Canceled by System' ||
        policyStageCode === 'Terminated' ||
        billingStageCode === 'Lapsed' ||
        policyStageCode === 'WaiverOfPremium' ||
        billingStageCode === 'WaiverOfPremium' ||
        policyGeneralStatus === 'WaiverOfPremium';
    }
  }

  setCACIncludeAdbr(includeAdbr: boolean) {
    this.includeAdbr = includeAdbr;
  }

  setCACWavierOfPremium(waiverOfPremium: boolean) {
    this.hasWaiver = waiverOfPremium;
  }
}
